import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import axios from 'axios';
import DropzoneAreaElement from 'custom-elements/dropzone-area-element';

@registerElement('user-onboarding')
export default class UserOnboardingElement extends ImpulseElement {
  @property() firstName: string;
  @property() lastName: string;
  @property() orgLogoUploadSrc: string;
  @property({ type: Array }) occupations: string[];

  @target() nameContainer?: HTMLElement;
  @target() orgNameContainer?: HTMLElement;
  @target() dropzone?: DropzoneAreaElement;
  @target() orgLogo?: HTMLImageElement;
  @target() uploadingField?: HTMLElement;
  @target() successField?: HTMLElement;
  @target() errorField?: HTMLElement;
  @target() occupationsContainer?: HTMLElement;

  private nameHash = { firstName: '', lastName: '' };
  private reader = new FileReader();
  private occupationSet = new Set<string>();

  constructor() {
    super();
    this.showOrgLogoPreview = this.showOrgLogoPreview.bind(this);
    this.reader.addEventListener('load', this.showOrgLogoPreview);
  }

  connected() {
    this.nameHash.firstName = this.firstName;
    this.nameHash.lastName = this.lastName;
    this.renderName();
    this.initializeOccupations();
  }

  disconnected(): void {
    this.reader.removeEventListener('load', this.showOrgLogoPreview);
    this.occupationSet.clear();
  }

  updateName(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const { type } = input.dataset;
    if (!type) return;
    this.nameHash[type as keyof typeof this.nameHash] = input.value;
    this.renderName();
  }

  updateOrgName(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.renderOrgName(input.value);
  }

  updateOccupation(event: Event) {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;
    const value = checkbox.dataset.value || '';

    if (checkbox.checked) {
      this.occupationSet.add(value);
    } else {
      this.occupationSet.delete(value);
    }

    this.renderOccupations();
  }

  uploading() {
    this.uploadingFieldHidden = false;
    this.errorFieldHidden = true;
    this.successFieldHidden = true;
  }

  async uploadSuccess(event: CustomEvent<{ signedId: string; file: Blob }>) {
    const { signedId, file } = event.detail;
    const formData = new FormData();
    formData.append('organization[logo]', signedId);

    try {
      await axios.patch(this.orgLogoUploadSrc, formData);
      this.reader.readAsDataURL(file);
      this.errorFieldHidden = true;
      this.successFieldHidden = false;
    } catch (error) {
      this.errorFieldHidden = false;
      this.successFieldHidden = true;
    } finally {
      this.dropzone?.removeAllFiles(); // Reset dropzone
      this.uploadingFieldHidden = true;
    }
  }

  uploadError() {
    this.errorFieldHidden = false;
    this.successFieldHidden = true;
  }

  private showOrgLogoPreview(event: ProgressEvent<FileReader>) {
    if (!this.orgLogo) return;
    this.orgLogo.src = event.target?.result as string;
  }

  private renderName() {
    const fullName = `${this.nameHash.firstName} ${this.nameHash.lastName}`.trim();
    if (!this.nameContainer) return;
    this.nameContainer.innerText = fullName;
  }

  private initializeOccupations() {
    this.occupations.forEach((value) => this.occupationSet.add(value));
    this.renderOccupations();
  }

  private renderOrgName(value: string) {
    if (!this.orgNameContainer) return;
    this.orgNameContainer.innerText = value;
  }

  private renderOccupations() {
    const occupations = Array.from(this.occupationSet).sort();
    if (!this.occupationsContainer) return;

    if (occupations.length > 0) {
      this.occupationsContainer.innerText = occupations.join(', ');
      this.occupationsContainer.hidden = false;
    } else {
      this.occupationsContainer.hidden = true;
    }
  }

  private set uploadingFieldHidden(value: boolean) {
    if (!this.uploadingField) return;
    this.uploadingField.hidden = value;
  }

  private set successFieldHidden(value: boolean) {
    if (!this.successField) return;
    this.successField.hidden = value;
  }

  private set errorFieldHidden(value: boolean) {
    if (!this.errorField) return;
    this.errorField.hidden = value;
  }
}
